import React, { useState, useEffect, useRef } from 'react';
import './BankTransferModal.css';

/**
 * Bank Transfer Modal - Cyberpunk Edition
 * A robust, visually stunning bank transfer interface
 */
const BankTransferModal = ({ isOpen, onClose, planData, onSuccess }) => {
    const [selectedBank, setSelectedBank] = useState('');
    const [receipt, setReceipt] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');
    const [copiedField, setCopiedField] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [step, setStep] = useState(1); // 1: Select Bank, 2: Transfer Details, 3: Upload Receipt
    const [verificationStatus, setVerificationStatus] = useState('pending'); // pending, verifying, verified
    const fileInputRef = useRef(null);

    // Extended bank list with more details
    const banks = [
        { 
            code: '030', 
            name: 'Ecobank Ghana', 
            account: '1234567890123',
            accountName: 'KAMELOT LIMITED',
            branch: 'Accra Main Branch',
            swift: 'ECOCGHAC',
            logo: '🏦',
            color: '#0066B3'
        },
        { 
            code: '011', 
            name: 'GCB Bank', 
            account: '0987654321098',
            accountName: 'KAMELOT LIMITED',
            branch: 'Osu Branch',
            swift: 'GHCBGHAC',
            logo: '🏛️',
            color: '#E31937'
        },
        { 
            code: '002', 
            name: 'Standard Chartered', 
            account: '5678901234567',
            accountName: 'KAMELOT LIMITED',
            branch: 'Airport City',
            swift: 'SCBLGHAC',
            logo: '🌐',
            color: '#00A3E0'
        },
        { 
            code: '004', 
            name: 'Barclays Bank (Absa)', 
            account: '1122334455667',
            accountName: 'KAMELOT LIMITED',
            branch: 'Ring Road Central',
            swift: 'BARCGHAC',
            logo: '🔷',
            color: '#C9002B'
        },
        { 
            code: '012', 
            name: 'Zenith Bank', 
            account: '9988776655443',
            accountName: 'KAMELOT LIMITED',
            branch: 'East Legon',
            swift: 'ZEIBGHAC',
            logo: '💎',
            color: '#A100FF'
        },
        { 
            code: '013', 
            name: 'Fidelity Bank', 
            account: '5544332211009',
            accountName: 'KAMELOT LIMITED',
            branch: 'Tema Main',
            swift: 'FBLIGHAC',
            logo: '🏪',
            color: '#FF6B00'
        }
    ];

    useEffect(() => {
        if (isOpen) {
            resetForm();
        }
    }, [isOpen]);

    const resetForm = () => {
        setSelectedBank('');
        setReceipt(null);
        setUploading(false);
        setUploadProgress(0);
        setReference(`BT-${Date.now().toString(36).toUpperCase().slice(-8)}`);
        setNotes('');
        setCopiedField(null);
        setStep(1);
        setVerificationStatus('pending');
    };

    const handleCopy = (text, field) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    // Drag and drop handlers
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            validateAndSetFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            validateAndSetFile(e.target.files[0]);
        }
    };

    const validateAndSetFile = (file) => {
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
        const maxSize = 5 * 1024 * 1024; // 5MB

        if (!allowedTypes.includes(file.type)) {
            showNotification('Invalid file type. Please upload JPG, PNG, or PDF.', 'error');
            return;
        }

        if (file.size > maxSize) {
            showNotification('File too large. Maximum size is 5MB.', 'error');
            return;
        }

        setReceipt(file);
    };

    const showNotification = (message, type = 'info') => {
        // Simple notification - can be replaced with proper notification system
        const notification = document.createElement('div');
        notification.className = `bt-notification ${type}`;
        const iconClass = type === 'success' ? 'check-circle' : type === 'error' ? 'exclamation-circle' : 'info-circle';
        notification.innerHTML = `
            <i class="fas fa-${iconClass}"></i>
            <span>${message}</span>
        `;
        document.body.appendChild(notification);
        setTimeout(() => {
            notification.classList.add('show');
            setTimeout(() => {
                notification.classList.remove('show');
                setTimeout(() => notification.remove(), 300);
            }, 3000);
        }, 100);
    };

    const handleUpload = async () => {
        if (!receipt) {
            showNotification('Please select a receipt file', 'error');
            return;
        }

        setUploading(true);
        setVerificationStatus('verifying');

        // Simulate upload progress
        const interval = setInterval(() => {
            setUploadProgress(prev => {
                if (prev >= 100) {
                    clearInterval(interval);
                    return 100;
                }
                return prev + Math.random() * 15 + 5;
            });
        }, 300);

        // Simulate processing time
        setTimeout(() => {
            clearInterval(interval);
            setUploadProgress(100);
            
            setTimeout(() => {
                setVerificationStatus('verified');
                setUploading(false);
                
                setTimeout(() => {
                    if (onSuccess) {
                        onSuccess({
                            reference,
                            bank: selectedBankData?.name,
                            amount: planData?.price,
                            receiptName: receipt.name
                        });
                    }
                    onClose();
                    showNotification('Receipt submitted successfully! Verification in progress.', 'success');
                }, 1500);
            }, 800);
        }, 2500);
    };

    const selectedBankData = banks.find(b => b.code === selectedBank);

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const getStepStatus = (stepNum) => {
        if (step === stepNum) return 'active';
        if (step > stepNum) return 'completed';
        return 'pending';
    };

    if (!isOpen || !planData) return null;

    return (
        <div className="bank-transfer-overlay" onClick={onClose}>
            <div className="bank-transfer-modal" onClick={e => e.stopPropagation()}>
                {/* Animated Background */}
                <div className="bt-bg-effects">
                    <div className="bt-grid"></div>
                    <div className="bt-glow"></div>
                    <div className="bt-particles">
                        {[...Array(8)].map((_, i) => (
                            <span key={i} className={`bt-particle p${i + 1}`}></span>
                        ))}
                    </div>
                </div>

                {/* Header */}
                <div className="bt-header">
                    <div className="bt-header-content">
                        <div className="bt-icon-wrapper">
                            <div className="bt-icon-ring"></div>
                            <i className="fas fa-university"></i>
                        </div>
                        <div className="bt-header-text">
                            <h2>Bank Transfer</h2>
                            <p>Secure Payment via Bank Transfer</p>
                        </div>
                    </div>
                    <button className="bt-close-btn" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* Progress Steps */}
                <div className="bt-steps">
                    {[
                        { num: 1, label: 'Select Bank', icon: 'fa-university' },
                        { num: 2, label: 'Transfer Details', icon: 'fa-exchange-alt' },
                        { num: 3, label: 'Upload Receipt', icon: 'fa-upload' }
                    ].map((s) => (
                        <div key={s.num} className={`bt-step ${getStepStatus(s.num)}`}>
                            <div className="bt-step-number">
                                {getStepStatus(s.num) === 'completed' ? (
                                    <i className="fas fa-check"></i>
                                ) : (
                                    <i className={`fas ${s.icon}`}></i>
                                )}
                            </div>
                            <span className="bt-step-label">{s.label}</span>
                            {s.num < 3 && <div className="bt-step-line"></div>}
                        </div>
                    ))}
                </div>

                {/* Content Body */}
                <div className="bt-body">
                    {/* Amount Card */}
                    <div className="bt-amount-card">
                        <div className="amount-label">
                            <i className="fas fa-shopping-cart"></i>
                            Payment Amount
                        </div>
                        <div className="amount-value">
                            <span className="currency">GH₵</span>
                            <span className="number">{planData.price}</span>
                            <span className="decimal">.00</span>
                        </div>
                        <div className="plan-badge">
                            <i className="fas fa-crown"></i>
                            {planData.name} Plan
                        </div>
                    </div>

                    {/* Step 1: Select Bank */}
                    {step === 1 && (
                        <div className="bt-step-content animate-in">
                            <h3 className="bt-section-title">
                                <i className="fas fa-landmark"></i>
                                Select Your Bank
                            </h3>
                            
                            <div className="bt-bank-grid">
                                {banks.map((bank) => (
                                    <div
                                        key={bank.code}
                                        className={`bt-bank-card ${selectedBank === bank.code ? 'selected' : ''}`}
                                        onClick={() => {
                                            setSelectedBank(bank.code);
                                            setTimeout(() => setStep(2), 400);
                                        }}
                                        style={{ '--bank-color': bank.color }}
                                    >
                                        <div className="bank-logo">{bank.logo}</div>
                                        <div className="bank-name">{bank.name}</div>
                                        <div className="bank-check">
                                            <i className="fas fa-check-circle"></i>
                                        </div>
                                        <div className="bank-glow"></div>
                                    </div>
                                ))}
                            </div>

                            <div className="bt-info-box info">
                                <i className="fas fa-info-circle"></i>
                                <p>Select your preferred bank to view transfer details. All transactions are secured with 256-bit encryption.</p>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Transfer Details */}
                    {step === 2 && selectedBankData && (
                        <div className="bt-step-content animate-in">
                            <h3 className="bt-section-title">
                                <i className="fas fa-clipboard-list"></i>
                                Transfer Instructions
                            </h3>

                            <div className="bt-bank-details">
                                <div className="bank-header" style={{ '--bank-color': selectedBankData.color }}>
                                    <span className="bank-logo-large">{selectedBankData.logo}</span>
                                    <span className="bank-title">{selectedBankData.name}</span>
                                </div>

                                <div className="details-list">
                                    <div className="detail-item">
                                        <span className="detail-icon"><i className="fas fa-building"></i></span>
                                        <div className="detail-info">
                                            <label>Account Name</label>
                                            <span className="detail-value">{selectedBankData.accountName}</span>
                                        </div>
                                    </div>

                                    <div className="detail-item highlight">
                                        <span className="detail-icon"><i className="fas fa-hashtag"></i></span>
                                        <div className="detail-info">
                                            <label>Account Number</label>
                                            <div className="copy-wrapper">
                                                <span className="detail-value mono">{selectedBankData.account}</span>
                                                <button 
                                                    className={`copy-btn ${copiedField === 'account' ? 'copied' : ''}`}
                                                    onClick={() => handleCopy(selectedBankData.account, 'account')}
                                                >
                                                    <i className={`fas ${copiedField === 'account' ? 'fa-check' : 'fa-copy'}`}></i>
                                                    <span>{copiedField === 'account' ? 'Copied!' : 'Copy'}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="detail-item">
                                        <span className="detail-icon"><i className="fas fa-map-marker-alt"></i></span>
                                        <div className="detail-info">
                                            <label>Branch</label>
                                            <span className="detail-value">{selectedBankData.branch}</span>
                                        </div>
                                    </div>

                                    <div className="detail-item">
                                        <span className="detail-icon"><i className="fas fa-globe"></i></span>
                                        <div className="detail-info">
                                            <label>SWIFT Code</label>
                                            <div className="copy-wrapper">
                                                <span className="detail-value mono">{selectedBankData.swift}</span>
                                                <button 
                                                    className={`copy-btn ${copiedField === 'swift' ? 'copied' : ''}`}
                                                    onClick={() => handleCopy(selectedBankData.swift, 'swift')}
                                                >
                                                    <i className={`fas ${copiedField === 'swift' ? 'fa-check' : 'fa-copy'}`}></i>
                                                    <span>{copiedField === 'swift' ? 'Copied!' : 'Copy'}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="detail-item highlight-gold">
                                        <span className="detail-icon"><i className="fas fa-receipt"></i></span>
                                        <div className="detail-info">
                                            <label>Payment Reference <span className="required">*Required</span></label>
                                            <div className="copy-wrapper">
                                                <span className="detail-value mono ref">{reference}</span>
                                                <button 
                                                    className={`copy-btn ${copiedField === 'reference' ? 'copied' : ''}`}
                                                    onClick={() => handleCopy(reference, 'reference')}
                                                >
                                                    <i className={`fas ${copiedField === 'reference' ? 'fa-check' : 'fa-copy'}`}></i>
                                                    <span>{copiedField === 'reference' ? 'Copied!' : 'Copy'}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bt-info-box warning">
                                <i className="fas fa-exclamation-triangle"></i>
                                <p><strong>Important:</strong> Please include the reference number <strong>{reference}</strong> in your transfer description to ensure proper crediting.</p>
                            </div>

                            <div className="bt-actions">
                                <button className="bt-btn secondary" onClick={() => setStep(1)}>
                                    <i className="fas fa-arrow-left"></i>
                                    Change Bank
                                </button>
                                <button className="bt-btn primary" onClick={() => setStep(3)}>
                                    I've Made the Transfer
                                    <i className="fas fa-arrow-right"></i>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Upload Receipt */}
                    {step === 3 && (
                        <div className="bt-step-content animate-in">
                            <h3 className="bt-section-title">
                                <i className="fas fa-cloud-upload-alt"></i>
                                Upload Payment Receipt
                            </h3>

                            {!receipt ? (
                                <div 
                                    className={`bt-upload-zone ${dragActive ? 'drag-active' : ''}`}
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <input 
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/jpg,application/pdf"
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                    />
                                    <div className="upload-icon">
                                        <i className="fas fa-cloud-upload-alt"></i>
                                        <div className="upload-pulse"></div>
                                    </div>
                                    <h4>Drag & Drop your receipt here</h4>
                                    <p>or click to browse from your device</p>
                                    <div className="upload-formats">
                                        <span><i className="fas fa-image"></i> JPG</span>
                                        <span><i className="fas fa-image"></i> PNG</span>
                                        <span><i className="fas fa-file-pdf"></i> PDF</span>
                                    </div>
                                    <div className="upload-limit">
                                        <i className="fas fa-shield-alt"></i>
                                        Maximum file size: 5MB
                                    </div>
                                </div>
                            ) : (
                                <div className="bt-file-preview">
                                    <div className="file-card">
                                        <div className="file-icon">
                                            <i className={`fas fa-${receipt.type.includes('pdf') ? 'file-pdf pdf' : 'file-image image'}`}></i>
                                        </div>
                                        <div className="file-details">
                                            <div className="file-name" title={receipt.name}>{receipt.name}</div>
                                            <div className="file-meta">
                                                <span className="file-size">{formatFileSize(receipt.size)}</span>
                                                <span className="file-type">{receipt.type.split('/')[1].toUpperCase()}</span>
                                            </div>
                                        </div>
                                        {!uploading && (
                                            <button 
                                                className="file-remove"
                                                onClick={() => setReceipt(null)}
                                            >
                                                <i className="fas fa-trash-alt"></i>
                                            </button>
                                        )}
                                    </div>

                                    {uploading && (
                                        <div className="upload-progress-container">
                                            <div className="upload-status">
                                                <i className="fas fa-circle-notch fa-spin"></i>
                                                <span>
                                                    {verificationStatus === 'verifying' 
                                                        ? 'Verifying receipt...' 
                                                        : 'Uploading...'}
                                                </span>
                                            </div>
                                            <div className="upload-progress-bar">
                                                <div 
                                                    className="upload-progress-fill"
                                                    style={{ width: `${Math.min(uploadProgress, 100)}%` }}
                                                ></div>
                                            </div>
                                            <div className="upload-percentage">{Math.round(uploadProgress)}%</div>
                                        </div>
                                    )}

                                    {verificationStatus === 'verified' && (
                                        <div className="verification-success">
                                            <i className="fas fa-check-circle"></i>
                                            <span>Verification Complete!</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="bt-form-group">
                                <label className="bt-label">
                                    <i className="fas fa-comment-alt"></i>
                                    Additional Notes <span className="optional">(Optional)</span>
                                </label>
                                <textarea
                                    className="bt-textarea"
                                    placeholder="Add any additional information about your transfer (e.g., transaction ID, sender name, etc.)"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    disabled={uploading}
                                    rows={3}
                                />
                            </div>

                            <div className="bt-actions">
                                <button className="bt-btn secondary" onClick={() => setStep(2)} disabled={uploading}>
                                    <i className="fas fa-arrow-left"></i>
                                    Back
                                </button>
                                <button 
                                    className="bt-btn primary submit"
                                    onClick={handleUpload}
                                    disabled={!receipt || uploading}
                                >
                                    {uploading ? (
                                        <>
                                            <i className="fas fa-circle-notch fa-spin"></i>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-paper-plane"></i>
                                            Submit Receipt
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="bt-footer">
                    <div className="bt-security">
                        <i className="fas fa-shield-alt"></i>
                        <span>256-bit SSL Encryption</span>
                    </div>
                    <div className="bt-trust">
                        <i className="fas fa-lock"></i>
                        <span>Secure Transaction</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BankTransferModal;
